import React, { useRef } from "react";
import { UserProfile } from "@clerk/clerk-react";
import { useWindowResponsive } from "@/hooks/useWindowResponsive";
import { useSafeWindowManager } from "@/hooks/useSafeWindowManager";

interface AccountManagementWindowProps {
  onClose: () => void;
}

export default function AccountManagementWindow({ onClose }: AccountManagementWindowProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const responsive = useWindowResponsive(containerRef);
  const { windowManager } = useSafeWindowManager();

  const handleOpenRoleManagement = () => {
    import('./RoleManagementWindow').then(({ default: RoleManagementWindow }) => {
      windowManager.openWindow(
        'role-management',
        'Role Management',
        React.createElement(RoleManagementWindow, {
          onClose: () => windowManager.closeWindow('role-management')
        }),
        {
          appType: 'role-management',
          menuConfig: {}
        }
      );
    });
  };

  const handleOpenUserRoles = () => {
    import('./UserRoleManagementWindow').then(({ default: UserRoleManagementWindow }) => {
      windowManager.openWindow(
        'user-role-management',
        'User Role Management',
        React.createElement(UserRoleManagementWindow, {
          onClose: () => windowManager.closeWindow('user-role-management')
        }),
        {
          appType: 'user-role-management',
          menuConfig: {}
        }
      );
    });
  };

  const handleOpenAuditLog = () => {
    import('./PermissionAuditLogWindow').then(({ default: PermissionAuditLogWindow }) => {
      windowManager.openWindow(
        'permission-audit-log',
        'Permission Audit Log',
        React.createElement(PermissionAuditLogWindow, {
          onClose: () => windowManager.closeWindow('permission-audit-log')
        }),
        {
          appType: 'permission-audit-log',
          menuConfig: {}
        }
      );
    });
  };
  // Calculate dynamic sizing based on window dimensions
  const getDynamicSizing = () => {
    const { isNarrow, isMedium } = responsive;
    
    if (isNarrow) return { fontSize: "11px", padding: "0.375rem", spacing: "0.5rem" };
    if (isMedium) return { fontSize: "12px", padding: "0.5rem", spacing: "0.75rem" };
    return { fontSize: "13px", padding: "0.75rem", spacing: "1rem" };
  };

  const sizing = getDynamicSizing();

  // Glass morphism appearance for Clerk's UserProfile
  const glassAppearance = {
    elements: {
      // Root container
      rootBox: {
        width: "100%",
        height: "100%",
        boxShadow: "none",
        backgroundColor: "transparent",
      },
      
      // Main card container
      card: {
        width: "100%",
        minHeight: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.20)",
        backdropFilter: "blur(20px)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        borderRadius: "8px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
        fontSize: sizing.fontSize,
      },
      
      // Sidebar navigation (show Clerk's native nav)
      navbar: {
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        backdropFilter: "blur(10px)",
        borderRight: "1px solid rgba(255, 255, 255, 0.1)",
        borderRadius: "8px 0 0 8px",
        padding: sizing.padding,
        minWidth: responsive.isNarrow ? "140px" : responsive.isMedium ? "160px" : "180px",
      },
      
      // Navigation items
      navbarMobileMenuButton: {
        color: "hsl(var(--foreground))",
      },
      
      navbarButton: {
        color: "hsl(var(--muted-foreground))",
        borderRadius: "6px",
        padding: responsive.isNarrow ? "0.5rem 0.75rem" : "0.75rem 1rem",
        margin: "0.125rem 0",
        transition: "all 0.3s ease",
        position: "relative",
        backgroundColor: "rgba(255, 255, 255, 0.05)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
        fontSize: sizing.fontSize,
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          color: "hsl(var(--foreground))",
          borderColor: "rgba(255, 255, 255, 0.2)",
          transform: "scale(1.02)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        },
        "&[data-active]": {
          backgroundColor: "rgba(255, 255, 255, 0.15)",
          color: "white",
          borderColor: "rgba(255, 255, 255, 0.3)",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
        }
      },
      
      // Page content area
      page: {
        backgroundColor: "transparent",
        color: "hsl(var(--foreground))",
        padding: sizing.spacing,
        fontSize: sizing.fontSize,
      },
      
      pageScrollBox: {
        backgroundColor: "transparent",
        padding: sizing.padding,
      },
      
      // Profile sections (cards within the content)
      profileSection: {
        backgroundColor: "rgba(0, 0, 0, 0.2)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        borderRadius: "6px",
        marginBottom: responsive.isNarrow ? "0.75rem" : "1rem",
        padding: responsive.isNarrow ? "1rem" : responsive.isMedium ? "1.25rem" : "1.5rem",
        boxShadow: "0 2px 12px rgba(0, 0, 0, 0.2)",
      },
      
      profileSectionTitle: {
        color: "hsl(var(--foreground))",
        fontSize: responsive.isNarrow ? "1rem" : "1.125rem",
        fontWeight: "600",
        marginBottom: responsive.isNarrow ? "0.75rem" : "1rem",
      },
      
      // Form elements
      formFieldInput: {
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        backdropFilter: "blur(5px)",
        borderColor: "rgba(255, 255, 255, 0.2)",
        color: "hsl(var(--foreground))",
        borderRadius: "6px",
        transition: "all 0.3s ease",
        "&:focus": {
          borderColor: "#cc5522",
          boxShadow: "0 0 0 2px rgba(204, 85, 34, 0.2), 0 2px 8px rgba(204, 85, 34, 0.15)",
        }
      },
      
      formFieldLabel: {
        color: "hsl(var(--foreground))",
        fontWeight: "500",
        fontSize: sizing.fontSize,
      },
      
      // Buttons with glass effect
      button: {
        backgroundColor: "rgba(255, 165, 0, 0.08)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 165, 0, 0.25)",
        borderRadius: "6px",
        padding: responsive.isNarrow ? "8px 16px" : responsive.isMedium ? "10px 20px" : "12px 24px",
        color: "rgb(255, 200, 87) !important",
        fontSize: sizing.fontSize,
        fontWeight: "500",
        cursor: "pointer",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative" as const,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        "&:hover": {
          backgroundColor: "rgba(255, 165, 0, 0.15)",
          borderColor: "rgba(255, 165, 0, 0.4)",
          transform: "scale(1.02)",
          color: "rgb(255, 200, 87) !important",
          boxShadow: "0 4px 16px rgba(255, 165, 0, 0.2)",
        },
        "&:active": {
          transform: "scale(0.98)",
        },
      },
      
      buttonSecondary: {
        backgroundColor: "rgba(255, 255, 255, 0.05)",
        color: "white !important",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        borderRadius: "8px",
        backdropFilter: "blur(10px)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          borderColor: "rgba(255, 255, 255, 0.3)",
          transform: "scale(1.02)",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
        },
        "&:active": {
          transform: "scale(0.98)",
        }
      },
      
      buttonDangerous: {
        backgroundColor: "rgba(239, 68, 68, 0.1)",
        color: "rgb(248, 113, 113) !important",
        border: "1px solid rgba(239, 68, 68, 0.3)",
        borderRadius: "8px",
        backdropFilter: "blur(10px)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        fontWeight: "500",
        "&:hover": {
          backgroundColor: "rgba(239, 68, 68, 0.2)",
          borderColor: "rgba(239, 68, 68, 0.5)",
          transform: "scale(1.02)",
          boxShadow: "0 4px 16px rgba(239, 68, 68, 0.2)",
          color: "rgb(248, 113, 113) !important",
        },
        "&:active": {
          transform: "scale(0.98)",
        }
      },

      // Add positive button style for save/success actions
      buttonSuccess: {
        backgroundColor: "rgba(34, 197, 94, 0.1)",
        color: "rgb(74, 222, 128) !important",
        border: "1px solid rgba(34, 197, 94, 0.3)",
        borderRadius: "8px",
        backdropFilter: "blur(10px)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        fontWeight: "500",
        "&:hover": {
          backgroundColor: "rgba(34, 197, 94, 0.2)",
          borderColor: "rgba(34, 197, 94, 0.5)",
          transform: "scale(1.02)",
          boxShadow: "0 4px 16px rgba(34, 197, 94, 0.2)",
          color: "rgb(74, 222, 128) !important",
        },
        "&:active": {
          transform: "scale(0.98)",
        }
      },
      
      // Avatar with glass border
      avatarBox: {
        border: "2px solid rgba(255, 255, 255, 0.2)",
        borderRadius: "50%",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
      },
      
      // Text elements
      text: {
        color: "hsl(var(--foreground))",
        fontSize: sizing.fontSize,
      },
      
      textSecondary: {
        color: "hsl(var(--muted-foreground))",
        fontSize: sizing.fontSize,
      },
    },
    
    variables: {
      colorPrimary: "#cc5522",
      colorBackground: "transparent",
      colorInputBackground: "rgba(0, 0, 0, 0.3)",
      colorInputText: "hsl(var(--foreground))",
      colorText: "hsl(var(--foreground))",
      colorTextSecondary: "hsl(var(--muted-foreground))",
      colorDanger: "hsl(var(--destructive))",
      borderRadius: "8px",
      fontSize: sizing.fontSize,
    },
  };

  return (
    <div 
      ref={containerRef}
      className="w-full min-h-full overflow-auto"
    >
      <UserProfile
        routing="hash"
        appearance={glassAppearance}
      >
        {/* Add custom page for Roles & Permissions */}
        <UserProfile.Page label="Roles & Permissions" labelIcon={<span>🔐</span>} url="roles-permissions">
          <div className="space-y-6 p-6">
            <div className="space-y-2">
              <h2 className="text-xl font-semibold tracking-tight text-foreground">Roles & Permissions</h2>
              <p className="text-sm text-muted-foreground">
                Manage roles, permissions, and access control for your organization
              </p>
            </div>

            {/* Admin Navigation Cards */}
            <div className="grid grid-cols-1 gap-4">
              {/* Role Management Card */}
              <div 
                className="p-6 bg-gradient-to-br from-primary/10 to-primary/5 backdrop-blur-xl border border-primary/20 rounded-lg hover:border-primary/40 transition-all cursor-pointer group"
                onClick={handleOpenRoleManagement}
              >
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <span className="text-xl">🛡️</span>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground">Role Management</h3>
                      <p className="text-xs text-muted-foreground">
                        Create and manage custom roles with specific permissions
                      </p>
                    </div>
                  </div>
                  <button className="text-xs text-primary hover:underline flex items-center gap-1">
                    Open Role Management →
                  </button>
                </div>
              </div>

              {/* User Role Assignment Card */}
              <div 
                className="p-6 bg-gradient-to-br from-blue-500/10 to-blue-500/5 backdrop-blur-xl border border-blue-500/20 rounded-lg hover:border-blue-500/40 transition-all cursor-pointer group"
                onClick={handleOpenUserRoles}
              >
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <span className="text-xl">👥</span>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground">User Roles</h3>
                      <p className="text-xs text-muted-foreground">
                        Assign and manage user roles within your organization
                      </p>
                    </div>
                  </div>
                  <button className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                    Manage User Roles →
                  </button>
                </div>
              </div>

              {/* Audit Log Card */}
              <div 
                className="p-6 bg-gradient-to-br from-purple-500/10 to-purple-500/5 backdrop-blur-xl border border-purple-500/20 rounded-lg hover:border-purple-500/40 transition-all cursor-pointer group"
                onClick={handleOpenAuditLog}
              >
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <span className="text-xl">📋</span>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground">Audit Log</h3>
                      <p className="text-xs text-muted-foreground">
                        View comprehensive logs of all permission changes
                      </p>
                    </div>
                  </div>
                  <button className="text-xs text-purple-500 hover:underline flex items-center gap-1">
                    View Audit Log →
                  </button>
                </div>
              </div>
            </div>

            {/* Info Banner */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <span className="text-lg">ℹ️</span>
                <div className="text-xs">
                  <p className="font-medium text-blue-400 mb-1">Permission System Active</p>
                  <p className="text-muted-foreground leading-relaxed">
                    The permission system uses role-based and attribute-based access control (RBAC + ABAC). 
                    All actions are logged and auditable for compliance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </UserProfile.Page>
      </UserProfile>
    </div>
  );
}